import React, {useEffect, useState} from 'react';
import {Form, Alert, Space} from 'antd';
import {Input} from 'components/Form';
import {Button} from 'ui';
import Spinner from 'components/Spinner';
import DefaultLayout from 'components/DefaultLayout';
import {useT} from 'hooks/Locale/useT';
import useQueryApiClient from 'utils/useApiClient';
import {SupportSettingsOwnerType, SupportSettingsValues} from '../../types';

interface SupportSettingsTabProps {
    type: SupportSettingsOwnerType;
    selectedResellerId: number;
    selectedCustomerId: number;
}

const emptyValues: SupportSettingsValues = {supportEmail: null, supportPhone: null};

const SupportSettingsTab: React.FC<SupportSettingsTabProps> = ({type, selectedResellerId, selectedCustomerId}) => {
    const t = useT();
    const [form] = Form.useForm();
    const [inherited, setInherited] = useState<SupportSettingsValues>(emptyValues);
    const [hasOwnSettings, setHasOwnSettings] = useState<boolean>(false);

    const ownerId = selectedCustomerId ? selectedCustomerId : selectedResellerId;
    const url = type === 'default' ? 'api/v1/support-settings/default' : `api/v1/support-settings/${type}/${ownerId}`;

    useEffect(() => {
        if (selectedResellerId !== 0 || selectedCustomerId !== 0) {
            fetch();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedResellerId, selectedCustomerId]);

    const handleResponse = (response: any) => {
        setInherited(response?.inherited ?? emptyValues);
        setHasOwnSettings(!!response?.hasOwnSettings);
        form.setFieldsValue(response?.settings ?? emptyValues);
    };

    const {fetch, isFetching} = useQueryApiClient({
        request: {
            disableOnMount: type !== 'default',
            url: url,
        },
        onSuccess: handleResponse,
    });

    const {appendData: submitForm, isLoading} = useQueryApiClient({
        request: {
            url: url,
            method: 'POST',
        },
        onSuccess: handleResponse,
    });

    const {appendData: deleteSettings, isLoading: isDeleting} = useQueryApiClient({
        request: {
            url: url,
            method: 'DELETE',
        },
        onSuccess: handleResponse,
    });

    // Value the owner falls back to when its own field is left empty
    const inheritedPlaceholder = (value: string | null) =>
        value
            ? `${t('system.environment.support_inherited_value')}: ${value}`
            : t('system.environment.support_not_set');

    return (
        <Spinner spinning={isFetching || isLoading || isDeleting}>
            {ownerId || type === 'default' ? (
                <>
                    {type !== 'default' && (
                        <Alert
                            className="mb-3"
                            message={t('system.environment.support_inherited_hint')}
                            type="info"
                            showIcon
                        />
                    )}
                    <Form form={form} onFinish={(values: SupportSettingsValues) => submitForm(values)}>
                        <Input
                            name="supportEmail"
                            label={t('system.environment.support_email')}
                            validation={{email: true}}
                            placeholder={type !== 'default' ? inheritedPlaceholder(inherited.supportEmail) : undefined}
                        />
                        <Input
                            name="supportPhone"
                            label={t('system.environment.support_phone')}
                            maxLength={50}
                            placeholder={type !== 'default' ? inheritedPlaceholder(inherited.supportPhone) : undefined}
                        />
                    </Form>
                    <DefaultLayout.PageFooter
                        right={
                            <Space>
                                {type !== 'default' && hasOwnSettings && (
                                    <Button
                                        message="system.environment.support_use_inherited"
                                        loading={isDeleting}
                                        onClick={() => deleteSettings()}
                                    />
                                )}
                                <Button
                                    type="primary"
                                    message="general.save"
                                    loading={isLoading}
                                    onClick={form.submit}
                                />
                            </Space>
                        }
                    />
                </>
            ) : (
                <Alert
                    message={t(`system.environment.please_select_a_${type === 'organization' ? 'reseller' : 'customer'}`)}
                    type="info"
                    showIcon
                />
            )}
        </Spinner>
    );
};

export default SupportSettingsTab;
